import React, { useEffect, useMemo, useRef, useState } from 'react';
import DropdownSelect, { type DropdownOption } from './DropdownSelect';
import DateRangePicker from './DateRangePicker';
import FilterChip from './FilterChip';
import ModalShell from './ModalShell';
import Button from './Button';
import { AnchoredPanel } from './Popover';
import { useIsMobile } from '../contexts/ViewportContext';
import { filterAddButtonClass, filterChipActiveClass } from '../lib/tableClasses';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * The one filter row of every list in Admin and Tenant.
 *
 *   desktop   [ search ] [Estado: activas ▾] [Gravedad: error ✕] [+ Filtro]
 *   mobile    [ search ....................................... (+) ]
 *             [ ⚙ Filtros 2 ] [Pendientes ✕] [Sept. ✕] → scrolls sideways
 *
 * WHY. Toolbars used to lay every filter out as its own h-10 dropdown. Past
 * four they wrapped into an unordered second and third line (Monitor had eight
 * controls), and on a phone every one took a full-width row above the list.
 * Here a filter only takes room once it is doing something.
 *
 * - **Desktop**: a chip per filter that is ON (plus the `pinned` ones, which
 *   always show — «Estado: activas» — and can be changed but not removed), and
 *   «+ Filtro» to add one. The chip IS the control: it opens the same list or
 *   calendar the old dropdown did (`variant="chip"` on `DropdownSelect` /
 *   `DateRangePicker`).
 * - **Mobile**: one «Filtros (n)» button that opens a panel with every filter
 *   as a full-width field. Changes are a DRAFT until «Ver resultados», because
 *   on a server-paged list each change is a request. The active ones also show
 *   as chips beside the button, so what is applied is never hidden.
 *
 * The screen keeps its own filter state; this only draws it. A filter is ON
 * when its value differs from `emptyValue` (default `''`).
 *
 * `custom` filters draw their own editor (the Monitor scope, which searches a
 * workshop as you type). They apply at once, also in the mobile panel — there
 * is no value this component could hold as a draft for them.
 *
 * Sorting is not a filter: tables sort from their column headers. The one
 * exception is a server list whose API only offers a few fixed orders (Monitor
 * → Alarmas): there it is a `pinned` «Orden» select, never a free dropdown.
 */

interface FilterBase {
    /** Stable id — also the React key. */
    key: string;
    /** Name shown on the chip and in the menus. */
    label: string;
    /** Material Symbol. */
    icon?: IconName;
    /** Always shown, never removable (changeable). */
    pinned?: boolean;
    /** Temporarily not offered (e.g. only on one tab). */
    hidden?: boolean;
    /**
     * Section heading in «+ Filtro» and in the mobile panel. Consecutive
     * filters with the same group share one heading; filters without a group
     * draw none. Only worth it past ~8 filters (the admin's Talleres list).
     */
    group?: string;
    /**
     * Column of «+ Filtro» on desktop. When any filter says `right` the menu
     * opens in two columns (left = the rest, in their order); a whole group
     * goes in one column, so set it on every filter of the group.
     */
    column?: 'left' | 'right';
    /**
     * `end` = drawn apart, at the far right of the row, always visible (like
     * `pinned`): a setting of WHICH list is shown rather than a filter that
     * comes and goes («Pruebas: Ocultos»), so the chips that do come and go
     * never reflow around it. On mobile it sits in the panel like the rest.
     */
    placement?: 'end';
}

export interface SelectFilter extends FilterBase {
    type?: 'select';
    value: string;
    onChange: (value: string) => void;
    options: DropdownOption[];
    /** The value that means "not filtering". Default `''`. */
    emptyValue?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
    emptyText?: string;
    /** Width of the option list. Default `w-64`. */
    dropdownWidth?: string;
}

export interface DateRangeFilter extends FilterBase {
    type: 'dateRange';
    start: string;
    end: string;
    onChange: (start: string, end: string) => void;
}

export interface CustomFilter extends FilterBase {
    type: 'custom';
    /** Is it narrowing the list right now. */
    active: boolean;
    /** Text after the colon on the chip. */
    valueLabel?: string;
    /** Turn it off. */
    onClear: () => void;
    /** The editor. `close` hides the desktop popover. */
    render: (close: () => void) => React.ReactNode;
    /** Popover width in px. Default 300. */
    width?: number;
}

export type FilterDef = SelectFilter | DateRangeFilter | CustomFilter;

export interface FilterBarLabels {
    addFilter: string;
    filters: string;
    clearAll: string;
    apply: string;
    remove: string;
    noMoreFilters: string;
    dateRangePlaceholder: string;
}

export const DEFAULT_FILTER_BAR_LABELS: FilterBarLabels = {
    addFilter: 'Filtro',
    filters: 'Filtros',
    clearAll: 'Quitar filtros',
    apply: 'Ver resultados',
    remove: 'Quitar filtro',
    noMoreFilters: 'No hay más filtros',
    dateRangePlaceholder: 'Rango de fechas',
};

/** Passed through to every `DateRangePicker` (the tenant localises them). */
export interface FilterBarDateLabels {
    monthNames?: string[];
    dayNames?: string[];
    clearLabel?: string;
    last7Label?: string;
    thisMonthLabel?: string;
}

export interface FilterBarProps {
    filters: FilterDef[];
    labels?: Partial<FilterBarLabels>;
    dateLabels?: FilterBarDateLabels;
    className?: string;
}

type Draft = Record<string, string | [string, string]>;

const isOn = (f: FilterDef): boolean => {
    if (f.type === 'dateRange') return !!(f.start || f.end);
    if (f.type === 'custom') return f.active;
    return f.value !== (f.emptyValue ?? '');
};

const clearOne = (f: FilterDef) => {
    if (f.type === 'dateRange') f.onChange('', '');
    else if (f.type === 'custom') f.onClear();
    else f.onChange(f.emptyValue ?? '');
};

const optionLabel = (f: SelectFilter, value: string) => f.options.find((o) => o.value === value)?.label ?? value;

/** Does item *i* open a new `group` (one heading per run of the same group)? */
const startsGroup = (list: FilterDef[], i: number): boolean => {
    const group = list[i]?.group;
    return !!group && (i === 0 || list[i - 1]?.group !== group);
};

const GroupHeading: React.FC<{ label: string; first: boolean }> = ({ label, first }) => (
    <p
        role="presentation"
        className={`px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 ${first ? 'pt-1' : 'pt-3'}`}
    >
        {label}
    </p>
);

/* ── Desktop ────────────────────────────────────────────────── */

const CustomChip: React.FC<{
    filter: CustomFilter;
    autoOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onRemove?: () => void;
    removeLabel: string;
}> = ({ filter, autoOpen, onOpenChange, onRemove, removeLabel }) => {
    const [open, setOpen] = useState(autoOpen);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const cb = useRef(onOpenChange);
    cb.current = onOpenChange;
    const openedOnce = useRef(false);
    useEffect(() => {
        if (!openedOnce.current && !open) return;
        openedOnce.current = true;
        cb.current(open);
    }, [open]);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            if (anchorRef.current?.parentElement?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            // Lists portalled from inside the editor (autocomplete, dropdown).
            if (t.closest('[data-dropdown-portal],[role="listbox"],.datepicker-range-portal')) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    return (
        <>
            <FilterChip
                ref={anchorRef}
                label={filter.label}
                value={filter.valueLabel || '—'}
                icon={filter.icon}
                active={filter.active}
                open={open}
                onRemove={onRemove}
                removeLabel={removeLabel}
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            />
            <AnchoredPanel anchorRef={anchorRef} open={open} width={filter.width ?? 300} label={filter.label}>
                <div ref={panelRef}>{filter.render(() => setOpen(false))}</div>
            </AnchoredPanel>
        </>
    );
};

const AddFilterMenu: React.FC<{
    candidates: FilterDef[];
    labels: FilterBarLabels;
    onPick: (key: string) => void;
}> = ({ candidates, labels, onPick }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const right = candidates.filter((f) => f.column === 'right');
    const left = candidates.filter((f) => f.column !== 'right');
    const twoColumns = right.length > 0 && left.length > 0;
    const columns = twoColumns ? [left, right] : [candidates];
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false);
                anchorRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    return (
        <>
            <button
                ref={anchorRef}
                type="button"
                className={filterAddButtonClass}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                <Icon name="add" className="!text-[18px]" aria-hidden="true" />
                {labels.addFilter}
            </button>
            <AnchoredPanel anchorRef={anchorRef} open={open} width={twoColumns ? 480 : 240} label={labels.addFilter}>
                <div
                    ref={panelRef}
                    role="menu"
                    className={`-m-1.5 max-h-[70vh] overflow-y-auto ${twoColumns ? 'grid grid-cols-2 gap-x-1' : ''}`}
                >
                    {candidates.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-400">{labels.noMoreFilters}</p>
                    ) : (
                        columns.map((col, c) => (
                            <div key={c} className="flex flex-col gap-0.5 min-w-0">
                                {col.map((f, i) => (
                                    <React.Fragment key={f.key}>
                                        {startsGroup(col, i) ? (
                                            <GroupHeading label={f.group as string} first={i === 0} />
                                        ) : null}
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                setOpen(false);
                                                onPick(f.key);
                                            }}
                                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                                        >
                                            <Icon name={f.icon ?? 'filter_list'} className="!text-[18px] text-slate-400 shrink-0" aria-hidden="true" />
                                            <span className="truncate">{f.label}</span>
                                        </button>
                                    </React.Fragment>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </AnchoredPanel>
        </>
    );
};

const DesktopBar: React.FC<{
    filters: FilterDef[];
    labels: FilterBarLabels;
    dateLabels?: FilterBarDateLabels;
    className: string;
}> = ({ filters, labels, dateLabels, className }) => {
    // The filter just picked from «+ Filtro»: shown with its editor open until
    // it gets a value, and dropped again if the editor closes without one.
    const [pending, setPending] = useState<string | null>(null);
    const atEnd = filters.filter((f) => f.placement === 'end');
    const inRow = filters.filter((f) => f.placement !== 'end');
    const shown = inRow.filter((f) => f.pinned || isOn(f) || f.key === pending);
    const candidates = inRow.filter((f) => !f.pinned && !isOn(f) && f.key !== pending);
    const anyRemovable = filters.some((f) => isOn(f));

    const closeEditor = (key: string) => (open: boolean) => {
        if (!open && key === pending) setPending(null);
    };

    const chip = (f: FilterDef) => {
        const removable = !f.pinned ? () => { clearOne(f); if (f.key === pending) setPending(null); } : undefined;
        const autoOpen = f.key === pending;
        if (f.type === 'dateRange') {
            return (
                <DateRangePicker
                    key={f.key}
                    variant="chip"
                    chipLabel={f.label}
                    startDate={f.start}
                    endDate={f.end}
                    onRangeChange={f.onChange}
                    onChipRemove={removable}
                    chipRemoveLabel={labels.remove}
                    autoOpen={autoOpen}
                    onOpenChange={closeEditor(f.key)}
                    placeholder={labels.dateRangePlaceholder}
                    {...dateLabels}
                />
            );
        }
        if (f.type === 'custom') {
            return (
                <CustomChip
                    key={f.key}
                    filter={f}
                    autoOpen={autoOpen}
                    onOpenChange={closeEditor(f.key)}
                    onRemove={removable}
                    removeLabel={labels.remove}
                />
            );
        }
        return (
            <DropdownSelect
                key={f.key}
                variant="chip"
                chipLabel={f.label}
                chipActive={isOn(f)}
                icon={f.icon}
                value={f.value}
                options={f.options}
                onChange={f.onChange}
                searchable={f.searchable}
                searchPlaceholder={f.searchPlaceholder}
                emptyText={f.emptyText}
                dropdownWidth={f.dropdownWidth ?? 'w-64'}
                onChipRemove={removable}
                chipRemoveLabel={labels.remove}
                autoOpen={autoOpen}
                onOpenChange={closeEditor(f.key)}
                placeholder="—"
            />
        );
    };

    return (
        <div className={`flex flex-wrap items-center gap-2 min-w-0 ${atEnd.length ? 'flex-1' : ''} ${className}`}>
            {shown.map(chip)}
            {candidates.length > 0 ? <AddFilterMenu candidates={candidates} labels={labels} onPick={setPending} /> : null}
            {anyRemovable ? (
                <button
                    type="button"
                    onClick={() => filters.forEach((f) => isOn(f) && clearOne(f))}
                    className="h-9 px-2 text-xs font-semibold text-slate-500 hover:text-primary dark:text-slate-400 whitespace-nowrap"
                >
                    {labels.clearAll}
                </button>
            ) : null}
            {atEnd.length ? <div className="ml-auto flex items-center gap-2">{atEnd.map(chip)}</div> : null}
        </div>
    );
};

/* ── Mobile ─────────────────────────────────────────────────── */

const draftOf = (filters: FilterDef[]): Draft => {
    const d: Draft = {};
    for (const f of filters) {
        if (f.type === 'dateRange') d[f.key] = [f.start, f.end];
        else if (f.type !== 'custom') d[f.key] = f.value;
    }
    return d;
};

const MobileBar: React.FC<{
    filters: FilterDef[];
    labels: FilterBarLabels;
    dateLabels?: FilterBarDateLabels;
    className: string;
}> = ({ filters, labels, dateLabels, className }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<Draft>({});
    const active = filters.filter(isOn);
    // Pinned filters at their default are not "applied" — they do not count.
    const count = active.length;

    const openPanel = () => {
        setDraft(draftOf(filters));
        setOpen(true);
    };

    const apply = () => {
        for (const f of filters) {
            if (f.type === 'custom') continue;
            const v = draft[f.key];
            if (f.type === 'dateRange') {
                const [s, e] = (v as [string, string]) ?? ['', ''];
                if (s !== f.start || e !== f.end) f.onChange(s, e);
            } else if (typeof v === 'string' && v !== f.value) {
                f.onChange(v);
            }
        }
        setOpen(false);
    };

    const clearDraft = () => {
        const d: Draft = {};
        for (const f of filters) {
            if (f.type === 'dateRange') d[f.key] = ['', ''];
            else if (f.type === 'custom') {
                if (f.active) f.onClear();
            } else d[f.key] = f.emptyValue ?? '';
        }
        setDraft(d);
    };

    const chipValue = (f: FilterDef): string => {
        if (f.type === 'dateRange') return [f.start, f.end].filter(Boolean).join(' — ');
        if (f.type === 'custom') return f.valueLabel ?? f.label;
        return optionLabel(f, f.value);
    };

    return (
        <div className={`flex items-center gap-2 min-w-0 overflow-x-auto no-scrollbar -mx-1 px-1 ${className}`}>
            <button
                type="button"
                onClick={openPanel}
                aria-haspopup="dialog"
                className={`${filterAddButtonClass} !border-solid ${count ? filterChipActiveClass : ''}`}
            >
                <Icon name="tune" className="!text-[18px]" aria-hidden="true" />
                {labels.filters}
                {count ? (
                    <span className="min-w-5 h-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center">
                        {count}
                    </span>
                ) : null}
            </button>
            {active.map((f) => (
                <FilterChip
                    key={f.key}
                    label={f.label}
                    value={chipValue(f)}
                    active
                    onClick={openPanel}
                    onRemove={f.pinned ? undefined : () => clearOne(f)}
                    removeLabel={labels.remove}
                />
            ))}

            <ModalShell isOpen={open} onClose={() => setOpen(false)} title={labels.filters} size="auto" maxWidth="max-w-lg">
                <div className="flex flex-col gap-4">
                    {filters.map((f, i) => {
                        const field = (() => {
                            if (f.type === 'custom') {
                                return (
                                    <div key={f.key} className="flex flex-col gap-1.5">
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            {f.label}
                                        </span>
                                        {f.render(() => undefined)}
                                    </div>
                                );
                            }
                            if (f.type === 'dateRange') {
                                const [s, e] = (draft[f.key] as [string, string]) ?? [f.start, f.end];
                                return (
                                    <div key={f.key} className="flex flex-col gap-1.5">
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            {f.label}
                                        </span>
                                        <DateRangePicker
                                            startDate={s}
                                            endDate={e}
                                            onRangeChange={(ns, ne) => setDraft((d) => ({ ...d, [f.key]: [ns, ne] }))}
                                            placeholder={labels.dateRangePlaceholder}
                                            {...dateLabels}
                                        />
                                    </div>
                                );
                            }
                            const v = typeof draft[f.key] === 'string' ? (draft[f.key] as string) : f.value;
                            return (
                                <DropdownSelect
                                    key={f.key}
                                    label={f.label}
                                    icon={f.icon}
                                    value={v}
                                    options={f.options}
                                    onChange={(nv) => setDraft((d) => ({ ...d, [f.key]: nv }))}
                                    searchable={f.searchable}
                                    searchPlaceholder={f.searchPlaceholder}
                                    emptyText={f.emptyText}
                                    dropdownWidth="w-full"
                                    surface="transparent"
                                    placeholder="—"
                                />
                            );
                        })();
                        return (
                            <React.Fragment key={f.key}>
                                {startsGroup(filters, i) ? (
                                    <p className="-mb-2 pt-1 text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                        {f.group}
                                    </p>
                                ) : null}
                                {field}
                            </React.Fragment>
                        );
                    })}
                    <div className="flex items-center justify-between gap-3 pt-2">
                        <button
                            type="button"
                            onClick={clearDraft}
                            className="text-sm font-semibold text-slate-500 hover:text-primary dark:text-slate-400"
                        >
                            {labels.clearAll}
                        </button>
                        <Button type="button" variant="primary" onClick={apply}>
                            {labels.apply}
                        </Button>
                    </div>
                </div>
            </ModalShell>
        </div>
    );
};

const FilterBar: React.FC<FilterBarProps> = ({ filters, labels, dateLabels, className = '' }) => {
    const isMobile = useIsMobile();
    const l = useMemo(() => ({ ...DEFAULT_FILTER_BAR_LABELS, ...labels }), [labels]);
    const visible = filters.filter((f) => !f.hidden);
    if (visible.length === 0) return null;
    return isMobile ? (
        <MobileBar filters={visible} labels={l} dateLabels={dateLabels} className={className} />
    ) : (
        <DesktopBar filters={visible} labels={l} dateLabels={dateLabels} className={className} />
    );
};

export default FilterBar;
